import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useGetAttachedCompanies, type AttachedCompany } from 'Backend/users/getAttachedCompanies';
import { useOnClickOutsideSingle } from 'Components/UI_Library/ReusableHooks/useDetectOutsideClickSingle';
import { getCurrentCompanyId, searchString, setCurrentCompanyId } from 'Lib/Utils';
import { isOktaInternalAuthToken } from 'Lib/Utils/authToken';
import PaytronIcon from 'Components/UI_Library/Icons/PaytronIcon';
import classes from './AccountToggle.module.css';
import { getAttachedCompanies } from 'Backend/users/utils/attachedCompanies/getAttachedCompanies';
import { NotificationsContext } from 'Components/Notifications';
import TextInput from 'Components/UI_Library/FormElements/TextInput';
import { logger } from 'utils/logger';
import { useGetCompany } from 'Backend/company/getCompany';

type Props = {
    expanded?: boolean;
    mobile?: boolean;
    style?: 'premium' | 'businessAccount';
};

const AccountToggle = (props: Props) => {
    const notifications = useContext(NotificationsContext);
    const [currentLogo, setCurrentLogo] = useState<'empty' | string | null>(null);
    const [showOtherAccounts, setShowOtherAccounts] = useState(false);
    const otherAccountsPopup = useRef(null);
    const history = useNavigate();

    const { data: company, refetch: refetchCompany } = useGetCompany();

    const [searchValue, setSearchValue] = useState<string>('');

    const {
        refetch: refetchAttachedCompanies,
        isFetching: _, // Need to access changed value from hook to trigger rerender, which renders updated companies (accounts) from localstorage
    } = useGetAttachedCompanies();

    useOnClickOutsideSingle(otherAccountsPopup, () => setShowOtherAccounts(false));

    const isFenix = isOktaInternalAuthToken();
    const currentCompanyId = getCurrentCompanyId();
    const accounts = getAttachedCompanies(); // FIXME: should we just use the data from the hook instead?
    const currentAccount = accounts?.find((company) => company.companyId === currentCompanyId);

    useEffect(() => {
        setCurrentLogo(company?.logo || '');
    }, [company]);

    const handleChangeCompany = async (value: string) => {
        setCurrentCompanyId(value);
        const { data: companyRefetched } = await refetchCompany();
        setCurrentLogo(companyRefetched?.logo || '');
        notifications.getInitialData();
        history('/dashboard');
    };

    const handleNavigateCreateCompany = () => {
        history('/create');
    };

    const searchFilter = (item: AttachedCompany) => searchString(item.companyName ?? '', searchValue);

    return (
        <div
            data-testid="account-toggle"
            className={`${classes.accountToggle} ${props.expanded ? classes.expanded : ''} ${
                props.style ? classes[props.style] || '' : ''
            }`}
            onClick={() => {
                !showOtherAccounts &&
                    refetchAttachedCompanies({ cancelRefetch: false, throwOnError: false }) // prettier
                        .catch((error: unknown) =>
                            logger.warn("Couldn't refetch attached companies", { error, component: AccountToggle.name })
                        );
                setShowOtherAccounts(!showOtherAccounts);
            }}
        >
            <div className={`${classes.logo} ${props.mobile ? classes.mobileLogo : ''}`}>
                {currentLogo && currentLogo !== 'empty' ? (
                    <img src={currentLogo} alt="company logo" />
                ) : (
                    <PaytronIcon colour="blue" height="14px" />
                )}
            </div>

            <div className={`${classes.text} ${props.mobile ? classes.mobileText : ''}`}>
                {isFenix ? <span className={classes.label}>Client account</span> : null}
                <span>{currentAccount?.companyName}</span>
            </div>
            <div className={`${classes.toggle} ${props.mobile ? classes.mobileToggle : ''}`}>
                <div>
                    <div />
                    <div />
                </div>
                <div>
                    <div />
                    <div />
                </div>
            </div>
            {showOtherAccounts ? (
                <div
                    className={`${classes.otherAccounts} ${props.mobile ? classes.mobileOtherAccounts : ''}`}
                    ref={otherAccountsPopup}
                >
                    <label>Switch accounts</label>
                    <div className={classes.otherAccountList}>
                        <TextInput
                            small
                            value={searchValue}
                            onChange={(e: { target: { value: string } }) => setSearchValue(e.target.value)}
                            placeholder="Search company name"
                            autoFocus
                        />
                        {accounts?.length > 0 ? (
                            accounts
                                .filter(searchFilter)
                                .sort((a, b) => (a?.companyName || '').localeCompare(b?.companyName || ''))
                                .map((item) => (
                                    <button
                                        type="button"
                                        onClick={() => item.companyId && handleChangeCompany(item.companyId)} // Customer must choose a company
                                        key={item.companyId}
                                        value={item.companyId}
                                    >
                                        <span>{item.companyName}</span>
                                    </button>
                                ))
                        ) : (
                            <button type="button" disabled>
                                <span>No other accounts</span>
                            </button>
                        )}
                    </div>

                    <div className={classes.createAccount}>
                        <button type="button" onClick={handleNavigateCreateCompany}>
                            <span>Create another account</span>
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default AccountToggle;
